import {
  Box,
  CloseButton,
  SelectorButton,
  Text,
  ToggleButton,
  Transition,
  TransitionProps,
} from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { ThumbsDownSmall, ThumbsUpSmall } from '@wix/wix-ui-icons-common';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { TLocalesKeys } from '../../../../types';
import { logChatbotAnswerFeedbackClicked } from '../../../bi/loggers/logChatbotAnswerFeedbackClicked';
import { logChatbotAnswerFeedbackFreeText } from '../../../bi/loggers/logChatbotAnswerFeedbackFreeText';
import { logChatbotAnswerFeedbackPresented } from '../../../bi/loggers/logChatbotAnswerFeedbackPresented';
import { logChatbotAnswerFeedbackReasonButtonClicked } from '../../../bi/loggers/logChatbotAnswerFeedbackReasonButtonClicked';
import { errorsSlice } from '../../../common/reducers/errors';
import { selectItems } from '../../../common/selectors/chat';
import { ExtraChatMessageProps } from '../Chat';
import { AnswerFeedbackDataHooks } from './AnswerFeedback.dataHooks';
import { OtherReasonModal } from './OtherReasonModal';
import s from './styles.scss';
import { FeedbackType } from './types';

type AnswerFeedbackProps = {
  messageId: string;
  scrollToFn: ExtraChatMessageProps['scrollToFn'];
};

const exitAnimation: TransitionProps['exitAnimation'] = {
  fadeOut: {
    duration: 'fast01',
    easing: 'exitEasing',
  },
};
export const AnswerFeedback: FC<AnswerFeedbackProps> = React.memo((props) => {
  const { messageId, scrollToFn } = props;
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [feedback, setFeedback] = React.useState<FeedbackType | null>(null);
  const [isCompleted, setIsCompleted] = React.useState<boolean>(false);
  const reasonsRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const autoCloseErrorTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const items = useSelector(selectItems);
  const wasReasonsSectionVisible = useRef<boolean>(false);
  const store = useStore();

  const reasons = useMemo(
    () => ({
      didnt_understand_my_question: t<TLocalesKeys>(
        'answer_feedback_negative_reason_didnt_understand_my_question',
      ),
      incorrect_answer: t<TLocalesKeys>('answer_feedback_negative_reason_incorrect_answer'),
      solution_didnt_work: t<TLocalesKeys>('answer_feedback_negative_reason_solution_didnt_work'),
      hard_to_read: t<TLocalesKeys>('answer_feedback_negative_reason_hard_to_read'),
      other: t<TLocalesKeys>('answer_feedback_negative_reason_other'),
    }),
    [t],
  );

  const handleSuccessfulFinish = useCallback(() => {
    setIsCompleted(true);
    dispatch(
      errorsSlice.actions.addError({
        localKey: 'answer_feedback_success_confirmation_text',
        severity: 'success',
      }),
    );

    autoCloseErrorTimeoutRef.current = setTimeout(() => {
      dispatch(errorsSlice.actions.removeErrorByLocalKey('answer_feedback_success_confirmation_text'));
    }, 5000);
  }, [dispatch]);

  const handleThumbUpClick = useCallback(() => {
    if (feedback !== null) {
      // Prevents from sending feedback multiple times since designers didn't want to disable the button
      return;
    }
    setFeedback(FeedbackType.Positive);
    handleSuccessfulFinish();
    logChatbotAnswerFeedbackClicked(store.getState(), { messageId, voteType: 'up' });
  }, [feedback, handleSuccessfulFinish, messageId, store]);

  const handleThumbDownClick = useCallback(() => {
    if (feedback !== null) {
      // Prevents from sending feedback multiple times since designers didn't want to disable the button
      return;
    }
    setFeedback(FeedbackType.Negative);
    logChatbotAnswerFeedbackClicked(store.getState(), { messageId, voteType: 'down' });
  }, [feedback, store, messageId]);

  const onReasonClick = useCallback(
    (reasonType: keyof typeof reasons) => {
      logChatbotAnswerFeedbackReasonButtonClicked(store.getState(), { messageId, reasonType });

      if (reasonType === 'other') {
        setIsModalOpen(true);
      } else {
        handleSuccessfulFinish();
      }
    },
    [setIsModalOpen, handleSuccessfulFinish, store, messageId],
  );

  const handleSectionClose = useCallback(() => {
    setIsCompleted(true);
  }, []);

  const handleModalSubmit = useCallback(
    (text: string) => {
      setIsModalOpen(false);
      handleSuccessfulFinish();
      logChatbotAnswerFeedbackFreeText(store.getState(), { messageId, text, clickType: 'submit' });
    },
    [handleSuccessfulFinish, store, messageId],
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    logChatbotAnswerFeedbackFreeText(store.getState(), { messageId, clickType: 'cancel' });
  }, [store, messageId]);

  const isReasonsSectionVisible = feedback === FeedbackType.Negative && !isCompleted;
  wasReasonsSectionVisible.current = isReasonsSectionVisible;

  useEffect(() => {
    if (feedback === FeedbackType.Negative && !isCompleted) {
      scrollToFn?.(reasonsRef.current);
    }
  }, [scrollToFn, feedback, isCompleted]);

  useEffect(() => {
    return () => {
      if (autoCloseErrorTimeoutRef.current) {
        clearTimeout(autoCloseErrorTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (wasReasonsSectionVisible.current) {
      setIsCompleted(true);
    }
  }, [items]);

  useEffect(() => {
    logChatbotAnswerFeedbackPresented(store.getState(), { messageId });
  }, [store, messageId]);

  return (
    <Box direction="vertical" dataHook={AnswerFeedbackDataHooks.feedbackSection()}>
      <Box direction="horizontal" gap="1">
        {feedback === null || feedback === FeedbackType.Positive ? (
          <ToggleButton
            labelValue={t<TLocalesKeys>('answer_feedback_positive_cta_label')}
            shape="round"
            priority="primary"
            size="small"
            border
            selected={feedback === FeedbackType.Positive}
            onClick={handleThumbUpClick}
          >
            <ThumbsUpSmall data-hook={AnswerFeedbackDataHooks.thumbUpButton()} />
          </ToggleButton>
        ) : null}
        {feedback === null || feedback === FeedbackType.Negative ? (
          <ToggleButton
            labelValue={t<TLocalesKeys>('answer_feedback_negative_cta_label')}
            shape="round"
            priority="primary"
            size="small"
            border
            selected={feedback === FeedbackType.Negative}
            onClick={handleThumbDownClick}
          >
            <ThumbsDownSmall data-hook={AnswerFeedbackDataHooks.thumbDownButton()} />
          </ToggleButton>
        ) : null}
      </Box>
      <Transition
        show={isReasonsSectionVisible}
        animateOnLoad
        mountOnEnter={false}
        exitAnimation={exitAnimation}
      >
        <Box
          border="1px solid"
          borderColor="D70"
          borderRadius="6px"
          padding="2"
          direction="vertical"
          gap="2"
          marginTop="2"
          position="relative"
          ref={reasonsRef}
          // Using css animation for fade in to be consistent with the rest of the chatbot components
          className={s.fadeIn}
          dataHook={AnswerFeedbackDataHooks.reasonsSection()}
        >
          <Box position="absolute" top="9px" right="6px">
            <CloseButton
              size="medium"
              skin="dark"
              onClick={handleSectionClose}
              dataHook={AnswerFeedbackDataHooks.reasonsSectionCloseButton()}
            />
          </Box>
          <Text size="tiny" secondary weight="normal">
            {t<TLocalesKeys>('answer_feedback_negative_section_heading')}
          </Text>
          <Box direction="horizontal" gap="1" flexWrap="wrap">
            {Object.keys(reasons).map((key: keyof typeof reasons) => (
              <Box direction="vertical" key={key} className={s.selectorButton}>
                <SelectorButton
                  fullWidth
                  size="small"
                  onClick={() => onReasonClick(key)}
                  dataHook={AnswerFeedbackDataHooks.reasonButton({ id: key })}
                >
                  <Text size="tiny">{reasons[key]}</Text>
                </SelectorButton>
              </Box>
            ))}
          </Box>
        </Box>
      </Transition>
      <OtherReasonModal isOpen={isModalOpen} onSubmit={handleModalSubmit} onClose={handleModalClose} />
    </Box>
  );
});
